import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "categorySelect", "productSelect", "deliveryZoneInput", "mealComboIdField",
    "stockText", "quantityInput", "unitPriceInput", "subtotalInput",
    "lineItem", "template", "container",
    "grandTotal", "removeButton", "addButton", "totalAmountHidden",
    "invoiceCard", "invoiceCheckbox", "invoiceSection",
    "billingContainer", "billingCuit", "receptorType", "receptorTypeHint",
    "invoicePreviewBody", "previewButton"
  ]
  static values = {
    categories: Array,
    uncategorizedProducts: Array,
    billingDataUrl: String,
    invoicePreviewUrl: String
  }

  connect() {
    this.initializeExistingLineItems()
    this.calculateGrandTotal()

    const form = this.element.closest('form')
    if (form) {
      form.addEventListener('submit', (e) => {
        this.prepareFormForSubmit()
        this.calculateGrandTotal()
      })
    }
  }

  // Public API methods for the checkout wizard
  getGrandTotal() {
    this.calculateGrandTotal()
    return this.hasTotalAmountHiddenTarget ? parseFloat(this.totalAmountHiddenTarget.value) || 0 : 0
  }

  getItems() {
    const items = []
    this.lineItemTargets.forEach(li => {
      if (li.style.display === 'none') return
      const productSelect = li.querySelector('[data-sale-form-target="productSelect"]')
      const quantityInput = li.querySelector('[data-sale-form-target="quantityInput"]')
      const unitPriceInput = li.querySelector('[data-sale-form-target="unitPriceInput"]')
      const subtotalInput = li.querySelector('[data-sale-form-target="subtotalInput"]')

      if (productSelect && productSelect.selectedIndex > 0) {
        const selectedOption = productSelect.options[productSelect.selectedIndex]
        items.push({
          name: selectedOption.textContent.split(' - $')[0].trim(),
          quantity: parseInt(quantityInput?.value) || 1,
          unit_price: parseFloat(unitPriceInput?.value) || 0,
          subtotal: parseFloat(subtotalInput?.value) || 0
        })
      }
    })
    return items
  }

  isValid() {
    const items = this.getItems()
    return items.length > 0 && this.getGrandTotal() > 0
  }

  getFormData() {
    this.prepareFormForSubmit()
    this.calculateGrandTotal()
    const form = this.element.closest('form')
    if (!form) return null
    return new FormData(form)
  }

  getBillingCuit() {
    return this.hasBillingCuitTarget ? this.billingCuitTarget.value : null
  }

  getReceptorType() {
    return this.hasReceptorTypeTarget ? this.receptorTypeTarget.value : null
  }

  shouldInvoice() {
    return this.hasInvoiceCheckboxTarget ? this.invoiceCheckboxTarget.checked : false
  }

  async loadBillingData(patientId) {
    if (!this.billingDataUrlValue) return

    // Show the invoice card
    if (this.hasInvoiceCardTarget) {
      this.invoiceCardTarget.style.display = 'block'
    }

    // Show loading in billing container
    if (this.hasBillingContainerTarget) {
      this.billingContainerTarget.innerHTML = `
        <div class="text-center py-3 text-muted">
          <i class="fas fa-spinner fa-spin"></i> Cargando datos de facturación...
        </div>
      `
    }

    try {
      const response = await fetch(`${this.billingDataUrlValue}?patient_id=${patientId}`, {
        headers: {
          'Accept': 'text/html',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        }
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const html = await response.text()
      if (this.hasBillingContainerTarget) {
        this.billingContainerTarget.innerHTML = html
      }

      // Setup listeners on the newly loaded cuit-selector
      this.setupCuitSelectorListeners()
      this.updatePreviewButtonState()
    } catch (error) {
      console.error('Error loading billing data:', error)
      if (this.hasBillingContainerTarget) {
        this.billingContainerTarget.innerHTML = `
          <div class="alert alert-warning">
            <i class="fas fa-exclamation-triangle"></i> Error cargando datos de facturación.
          </div>
        `
      }
    }
  }

  setupCuitSelectorListeners() {
    const cuitSelectorEl = this.element.querySelector('[data-controller*="cuit-selector"]')
    if (!cuitSelectorEl) return

    cuitSelectorEl.addEventListener('cuit-selector:confirmed', (event) => {
      const ivaConditionId = event.detail?.ivaConditionId

      // Auto-set receptor type from confirmed IVA condition
      const ivaToReceptorMap = {
        '1': 'responsable_inscripto',
        '2': 'exento_iva',
        '3': 'consumidor_final',
        '4': 'monotributista'
      }
      const receptorType = ivaToReceptorMap[String(ivaConditionId)]
      if (receptorType && this.hasReceptorTypeTarget) {
        this.receptorTypeTarget.value = receptorType
        if (this.hasReceptorTypeHintTarget) {
          this.receptorTypeHintTarget.innerHTML =
            '<i class="fas fa-check-circle text-success"></i> Tipo de receptor configurado desde Colppy'
        }
      }

      this.updatePreviewButtonState()
    })

    cuitSelectorEl.addEventListener('cuit-selector:rejected', () => {
      this.updatePreviewButtonState()
    })

    cuitSelectorEl.addEventListener('cuit-selector:pending', () => {
      this.updatePreviewButtonState()
    })
  }

  toggleInvoiceSection() {
    if (!this.hasInvoiceSectionTarget || !this.hasInvoiceCheckboxTarget) return

    if (this.invoiceCheckboxTarget.checked) {
      this.invoiceSectionTarget.style.display = 'block'
    } else {
      this.invoiceSectionTarget.style.display = 'none'
    }
  }

  updatePreviewButtonState() {
    if (!this.hasPreviewButtonTarget) return

    const receptorType = this.hasReceptorTypeTarget ? this.receptorTypeTarget.value : null
    const billingCuit = this.hasBillingCuitTarget ? this.billingCuitTarget.value : null

    this.previewButtonTarget.disabled = !receptorType || !billingCuit
  }

  async previewInvoice() {
    if (!this.invoicePreviewUrlValue) return

    const patientIdInput = document.querySelector('#sale_patient_id')
    const patientId = patientIdInput ? patientIdInput.value : null
    const receptorType = this.hasReceptorTypeTarget ? this.receptorTypeTarget.value : ''
    const billingCuit = this.hasBillingCuitTarget ? this.billingCuitTarget.value : ''

    if (!patientId) {
      alert('Debe seleccionar un paciente')
      return
    }
    if (!receptorType) {
      alert('Debe seleccionar la condición IVA del receptor')
      return
    }
    if (!billingCuit) {
      alert('Debe seleccionar un CUIT de facturación')
      return
    }

    // Recalculate total
    this.calculateGrandTotal()
    const totalAmount = this.hasTotalAmountHiddenTarget ? this.totalAmountHiddenTarget.value : '0'

    // Build items array from visible line items
    const items = this.getItems()

    // Show loading in preview button
    if (this.hasPreviewButtonTarget) {
      this.previewButtonTarget.disabled = true
      this.previewButtonTarget.innerHTML = '<i class="fas fa-spinner fa-spin mr-1"></i> Cargando...'
    }

    try {
      const response = await fetch(this.invoicePreviewUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({
          patient_id: patientId,
          total_amount: totalAmount,
          receptor_type: receptorType,
          billing_cuit: billingCuit,
          items: JSON.stringify(items)
        })
      })

      const data = await response.json()
      this.renderInvoicePreview(data)

      // Show the modal
      $('#invoicePreviewModal').modal('show')
    } catch (error) {
      console.error('Error loading invoice preview:', error)
      alert('Error al cargar la previsualización de la factura')
    } finally {
      if (this.hasPreviewButtonTarget) {
        this.previewButtonTarget.disabled = false
        this.previewButtonTarget.innerHTML = '<i class="fas fa-eye mr-1"></i> Previsualizar Factura'
      }
    }
  }

  renderInvoicePreview(data) {
    if (!this.hasInvoicePreviewBodyTarget) return

    let itemsHtml = ''
    if (data.items && data.items.length > 0) {
      itemsHtml = `
        <table class="table table-sm mt-3">
          <thead><tr><th>Producto</th><th>Cant.</th><th>P. Unit.</th><th>Subtotal</th></tr></thead>
          <tbody>
            ${data.items.map(item => `
              <tr>
                <td>${item.name}</td>
                <td>${item.quantity}</td>
                <td>$${parseFloat(item.unit_price).toFixed(2)}</td>
                <td>$${parseFloat(item.subtotal).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `
    }

    this.invoicePreviewBodyTarget.innerHTML = `
      <div class="row mb-3">
        <div class="col-md-6">
          <p><strong>Paciente:</strong> ${data.patient_name}</p>
          <p><strong>CUIT:</strong> ${data.cuit || 'N/A'}</p>
        </div>
        <div class="col-md-6">
          <p><strong>Tipo:</strong> ${data.invoice_type}</p>
        </div>
      </div>
      ${itemsHtml}
      <hr>
      <div class="row">
        <div class="col-md-6 offset-md-6 text-right">
          <p><strong>Neto Gravado:</strong> $${parseFloat(data.neto_gravado).toFixed(2)}</p>
          <p><strong>IVA (21%):</strong> $${parseFloat(data.total_iva).toFixed(2)}</p>
          <p class="h5"><strong>Total:</strong> $${parseFloat(data.total).toFixed(2)}</p>
        </div>
      </div>
    `
  }

  initializeExistingLineItems() {
    // For existing line items that already have a category selected, filter the product options
    this.categorySelectTargets.forEach(select => {
      if (select.value) {
        const lineItem = select.closest('.line-item-fields')
        this.filterProductsForCategory(lineItem, select.value)
      }
    })
    this.updateProductOptions()
  }

  categoryChanged(event) {
    const select = event.target
    const categoryId = select.value
    const lineItem = select.closest('.line-item-fields')

    // Reset product select and hidden fields
    const productSelect = lineItem.querySelector('[data-sale-form-target="productSelect"]')
    const deliveryZoneInput = lineItem.querySelector('[data-sale-form-target="deliveryZoneInput"]')
    const mealComboIdField = lineItem.querySelector('[data-sale-form-target="mealComboIdField"]')
    const unitPriceInput = lineItem.querySelector('[data-sale-form-target="unitPriceInput"]')
    const stockText = lineItem.querySelector('[data-sale-form-target="stockText"]')

    if (productSelect) productSelect.value = ''
    if (deliveryZoneInput) deliveryZoneInput.value = ''
    if (mealComboIdField) mealComboIdField.value = ''
    if (unitPriceInput) unitPriceInput.value = ''
    if (stockText) stockText.textContent = ''

    this.filterProductsForCategory(lineItem, categoryId)
    this.calculateSubtotal(lineItem)
    this.calculateGrandTotal()
    this.updateProductOptions()
  }

  filterProductsForCategory(lineItem, categoryId) {
    const productSelect = lineItem.querySelector('[data-sale-form-target="productSelect"]')
    if (!productSelect) return

    if (!categoryId) {
      // No category selected — show all products from all regular categories
      productSelect.innerHTML = '<option value="">Seleccionar producto</option>'
      this.categoriesValue.forEach(cat => {
        if (cat.system_type) return
        ;(cat.items || []).forEach(p => {
          const option = document.createElement('option')
          option.value = p.id
          option.textContent = `${p.name} - $${p.price}`
          option.dataset.price = p.price
          option.dataset.categoryId = cat.id
          productSelect.appendChild(option)
        })
      })
      lineItem.dataset.isDeliveryZone = 'false'
      lineItem.dataset.isMealCombo = 'false'
      return
    }

    const category = this.categoriesValue.find(c => c.id == categoryId)
    if (!category) return

    if (category.system_type === 'delivery_zone') {
      lineItem.dataset.isDeliveryZone = 'true'
      lineItem.dataset.isMealCombo = 'false'
      this.populateWithDeliveryZones(productSelect, category.items)
    } else if (category.system_type === 'meal_combo') {
      lineItem.dataset.isDeliveryZone = 'false'
      lineItem.dataset.isMealCombo = 'true'
      this.populateWithMealCombos(productSelect, category.items)
    } else {
      lineItem.dataset.isDeliveryZone = 'false'
      lineItem.dataset.isMealCombo = 'false'
      this.populateWithCategoryProducts(productSelect, category)
    }
  }

  populateWithDeliveryZones(productSelect, zones) {
    productSelect.innerHTML = '<option value="">Seleccionar zona</option>'
    zones.forEach(zone => {
      const option = document.createElement('option')
      option.value = `dz_${zone.id}`
      option.textContent = `${zone.name} - $${zone.price}`
      option.dataset.price = zone.price
      option.dataset.deliveryZoneId = zone.id
      productSelect.appendChild(option)
    })
  }

  populateWithMealCombos(productSelect, combos) {
    productSelect.innerHTML = '<option value="">Seleccionar combo...</option>'
    combos.forEach(mc => {
      const option = document.createElement('option')
      option.value = `mc_${mc.id}`
      option.textContent = `${mc.name} - $${mc.price}`
      option.dataset.price = mc.price
      option.dataset.mealComboId = mc.id
      productSelect.appendChild(option)
    })
  }

  populateWithCategoryProducts(productSelect, category) {
    productSelect.innerHTML = '<option value="">Seleccionar producto</option>'
    if (category.items && category.items.length > 0) {
      category.items.forEach(p => {
        const option = document.createElement('option')
        option.value = p.id
        option.textContent = `${p.name} - $${p.price}`
        option.dataset.price = p.price
        option.dataset.categoryId = category.id
        if (p.stock !== undefined) option.dataset.stock = p.stock
        productSelect.appendChild(option)
      })
    }
  }

  productChanged(event) {
    const select = event.target
    const selectedOption = select.options[select.selectedIndex]
    const price = selectedOption.dataset.price || ''
    const lineItem = select.closest('.line-item-fields')
    const isDeliveryZone = lineItem.dataset.isDeliveryZone === 'true'
    const isMealCombo = lineItem.dataset.isMealCombo === 'true'

    const unitPriceInput = lineItem.querySelector('[data-sale-form-target="unitPriceInput"]')
    const quantityInput = lineItem.querySelector('[data-sale-form-target="quantityInput"]')
    const deliveryZoneInput = lineItem.querySelector('[data-sale-form-target="deliveryZoneInput"]')
    const mealComboIdField = lineItem.querySelector('[data-sale-form-target="mealComboIdField"]')
    const stockText = lineItem.querySelector('[data-sale-form-target="stockText"]')

    if (isDeliveryZone) {
      const deliveryZoneId = selectedOption.dataset.deliveryZoneId || ''
      if (deliveryZoneInput) deliveryZoneInput.value = deliveryZoneId
      if (mealComboIdField) mealComboIdField.value = ''
      if (stockText) stockText.textContent = ''
    } else if (isMealCombo) {
      const mealComboId = selectedOption.dataset.mealComboId || ''
      if (mealComboIdField) mealComboIdField.value = mealComboId
      if (deliveryZoneInput) deliveryZoneInput.value = ''
      if (stockText) stockText.textContent = ''
    } else {
      if (deliveryZoneInput) deliveryZoneInput.value = ''
      if (mealComboIdField) mealComboIdField.value = ''

      if (stockText && selectedOption.value) {
        const catSelect = lineItem.querySelector('[data-sale-form-target="categorySelect"]')
        const category = catSelect ? this.categoriesValue.find(c => c.id == catSelect.value) : null
        if (category) {
          const product = category.items.find(p => p.id == parseInt(selectedOption.value))
          if (product && product.stock !== undefined) {
            stockText.textContent = `Stock: ${product.stock}`
          } else {
            stockText.textContent = ''
          }
        } else {
          stockText.textContent = ''
        }
      } else if (stockText) {
        stockText.textContent = ''
      }
    }

    if (unitPriceInput) {
      unitPriceInput.value = price
    }

    if (quantityInput && quantityInput.value) {
      this.calculateSubtotal(lineItem)
    }

    this.updateProductOptions()
    this.calculateGrandTotal()
  }

  updateProductOptions() {
    const selectedProductIds = this.productSelectTargets
      .filter(select => {
        const lineItem = select.closest('.line-item-fields')
        return lineItem && lineItem.style.display !== 'none' &&
               lineItem.dataset.isDeliveryZone !== 'true' &&
               lineItem.dataset.isMealCombo !== 'true'
      })
      .map(select => select.value)
      .filter(value => value && value !== '')

    this.productSelectTargets.forEach(select => {
      const lineItem = select.closest('.line-item-fields')
      if (!lineItem || lineItem.dataset.isDeliveryZone === 'true' || lineItem.dataset.isMealCombo === 'true') return

      const currentValue = select.value

      select.querySelectorAll('option').forEach(option => {
        const optionValue = option.value

        if (optionValue === '' || optionValue === currentValue) return
        if (option.style.display === 'none' && !selectedProductIds.includes(optionValue)) return

        if (selectedProductIds.includes(optionValue) && optionValue !== currentValue) {
          option.style.display = 'none'
        }
      })
    })

    if (this.hasAddButtonTarget) {
      const hasCategories = this.categoriesValue.length > 0
      this.addButtonTarget.disabled = !hasCategories
      if (!hasCategories) {
        this.addButtonTarget.classList.add('disabled')
        this.addButtonTarget.style.opacity = '0.5'
        this.addButtonTarget.style.cursor = 'not-allowed'
      } else {
        this.addButtonTarget.classList.remove('disabled')
        this.addButtonTarget.style.opacity = '1'
        this.addButtonTarget.style.cursor = 'pointer'
      }
    }
  }

  calculateSubtotal(eventOrElement) {
    const lineItem = eventOrElement.target ? eventOrElement.target.closest('.line-item-fields') : eventOrElement
    const quantityInput = lineItem.querySelector('[data-sale-form-target="quantityInput"]')
    const unitPriceInput = lineItem.querySelector('[data-sale-form-target="unitPriceInput"]')
    const subtotalInput = lineItem.querySelector('[data-sale-form-target="subtotalInput"]')

    if (quantityInput && unitPriceInput && subtotalInput) {
      const quantity = parseInt(quantityInput.value) || 0
      const unitPrice = parseFloat(unitPriceInput.value) || 0
      const subtotal = quantity * unitPrice

      subtotalInput.value = subtotal.toFixed(2)
      this.calculateGrandTotal()
    }
  }

  calculateGrandTotal() {
    const visibleSubtotalInputs = this.subtotalInputTargets.filter(input => {
      const lineItem = input.closest('.line-item-fields')
      return lineItem && lineItem.style.display !== 'none'
    })

    const subtotals = visibleSubtotalInputs.map(input => parseFloat(input.value) || 0)
    const grandTotal = subtotals.reduce((sum, val) => sum + val, 0)

    const grandTotalElement = document.querySelector('[data-sale-form-target="grandTotal"]')
    if (grandTotalElement) {
      grandTotalElement.textContent = `$${grandTotal.toFixed(2)}`
    }

    if (this.hasTotalAmountHiddenTarget) {
      this.totalAmountHiddenTarget.value = grandTotal.toFixed(2)
    }
  }

  prepareFormForSubmit() {
    this.lineItemTargets.forEach(lineItem => {
      if (lineItem.style.display === 'none') return

      const isDeliveryZone = lineItem.dataset.isDeliveryZone === 'true'
      const isMealCombo = lineItem.dataset.isMealCombo === 'true'
      const productSelect = lineItem.querySelector('[data-sale-form-target="productSelect"]')
      const deliveryZoneInput = lineItem.querySelector('[data-sale-form-target="deliveryZoneInput"]')
      const mealComboIdField = lineItem.querySelector('[data-sale-form-target="mealComboIdField"]')

      if (isDeliveryZone && productSelect) {
        const selectedOption = productSelect.options[productSelect.selectedIndex]
        if (selectedOption && selectedOption.dataset.deliveryZoneId) {
          if (deliveryZoneInput) deliveryZoneInput.value = selectedOption.dataset.deliveryZoneId
        }
        productSelect.value = ''
      }

      if (isMealCombo && productSelect) {
        const selectedOption = productSelect.options[productSelect.selectedIndex]
        if (selectedOption && selectedOption.dataset.mealComboId) {
          if (mealComboIdField) mealComboIdField.value = selectedOption.dataset.mealComboId
        }
        productSelect.value = ''
      }
    })
  }

  addField(event) {
    event.preventDefault()

    const newField = this.templateTarget.innerHTML.replace(/TEMPLATE_RECORD/g, Date.now())
    this.containerTarget.insertAdjacentHTML('beforeend', newField)

    this.updateProductOptions()
  }

  removeField(event) {
    event.preventDefault()

    const lineItem = event.target.closest('.line-item-fields')
    const isFirstItem = lineItem.dataset.firstItem === 'true'

    if (isFirstItem) {
      alert('No puedes borrar el primer producto. Debe haber al menos un producto en la venta.')
      return
    }

    const destroyInput = lineItem.querySelector('input[name$="[_destroy]"]')
    const subtotalInput = lineItem.querySelector('[data-sale-form-target="subtotalInput"]')

    if (destroyInput) {
      destroyInput.value = '1'

      if (subtotalInput) {
        subtotalInput.value = '0.00'
      }

      lineItem.style.display = 'none'

      setTimeout(() => {
        this.updateProductOptions()
        this.calculateGrandTotal()
      }, 0)
    }
  }

  updateFileName(event) {
    const fileInput = event.target
    const displayField = document.getElementById('transfer_receipt_display')

    if (fileInput.files && fileInput.files.length > 0) {
      if (displayField) {
        displayField.value = fileInput.files[0].name
      }
    } else {
      if (displayField) {
        displayField.value = ''
        displayField.placeholder = 'Ningún archivo elegido'
      }
    }
  }

}
